import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCapabilities } from "@/lib/useCapabilities";
import { listPlatformLicenseRequests } from "@/lib/platformApi";
import { getModuleInicioPath, WORK_MODULES } from "@/modules/workModuleCore";

/**
 * Alerta para diseñador / Centro SaaS cuando hay solicitudes pendientes de revisión.
 */
export default function PlatformLicenseRequestsPendingBanner() {
  const { permissions, isPlatformDesigner, loading: capsLoading } = useCapabilities();
  const canSee = Boolean(isPlatformDesigner || permissions?.access_license_center);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!canSee) return;
    setLoading(true);
    try {
      const [pending, inReview] = await Promise.all([
        listPlatformLicenseRequests({ status: "pending" }),
        listPlatformLicenseRequests({ status: "in_review" }),
      ]);
      setPendingCount((pending?.length || 0) + (inReview?.length || 0));
    } catch {
      setPendingCount(0);
    } finally {
      setLoading(false);
    }
  }, [canSee]);

  useEffect(() => {
    if (capsLoading || !canSee) return undefined;
    reload();
    return undefined;
  }, [capsLoading, canSee, reload]);

  if (capsLoading || !canSee || (!loading && pendingCount === 0)) {
    return null;
  }

  const label =
    pendingCount === 1
      ? "Tienes 1 solicitud de licencia pendiente de revisión en Centro SaaS BNT."
      : `Tienes ${pendingCount} solicitudes de licencia pendientes de revisión en Centro SaaS BNT.`;

  return (
    <div
      className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex flex-wrap items-center justify-between gap-2 shrink-0"
      data-testid="platform-license-requests-pending-banner"
    >
      <p className="text-xs text-amber-950 m-0">
        {loading ? (
          <span className="inline-flex items-center gap-1.5">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Revisando solicitudes comerciales…
          </span>
        ) : (
          <>
            <span className="font-semibold">Solicitudes comerciales:</span> {label}
          </>
        )}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          asChild
          type="button"
          size="sm"
          variant="outline"
          className="h-8 text-xs border-amber-400 text-amber-950 hover:bg-amber-100"
        >
          <Link to="/platform/license-requests" data-testid="platform-license-requests-pending-open">
            Ver solicitudes
          </Link>
        </Button>
        {!isPlatformDesigner ? (
          <Button
            asChild
            type="button"
            size="sm"
            variant="outline"
            className="h-8 text-xs border-amber-400 text-amber-950 hover:bg-amber-100"
          >
            <Link
              to={getModuleInicioPath(WORK_MODULES.DISENADOR)}
              data-testid="platform-license-requests-pending-designer"
            >
              Cambiar a modo Diseñador
            </Link>
          </Button>
        ) : null}
      </div>
    </div>
  );
}
